/// <reference types="vite/client" />

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';

// Store CSRF token in memory (not localStorage to avoid XSS)
let csrfToken: string | null = null;

// Helper function to get CSRF token from cookies or memory
const getCsrfToken = (): string | null => {
  // First try memory
  if (csrfToken) {
    return csrfToken;
  }

  // Fall back to cookies (for local dev)
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

// Set CSRF token in memory
export const setCsrfToken = (token: string) => {
  csrfToken = token;
};

// The single source of truth for all API calls.
export const customFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const fullUrl = `${API_BASE_URL}${url}`;
  
  const headers = new Headers(options.headers);

  const token = localStorage.getItem('token');
  if (token) {
    headers.set('Authorization', `Token ${token}`);
  }

  // Add CSRF token for non-GET requests
  if (options.method && options.method.toUpperCase() !== 'GET' && !headers.has('X-CSRFToken')) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers.set('X-CSRFToken', csrfToken);
    }
  }

  // Only set JSON content type if it's not a FormData request
  // For FormData, let the browser set the Content-Type with proper boundary
  if (!(options.body instanceof FormData)) {
    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
  }

  const response = await fetch(fullUrl, {
    ...options,
    headers: headers,
    credentials: 'include', // Include cookies for CSRF
  });

  return response;
};

// The upload function is now simplified and also uses the central token logic.
export const uploadFiles = async (
  url: string,
  files: File[],
  metadata: Record<string, any> = {}
): Promise<Response> => {
  const formData = new FormData();

  files.forEach(file => {
    if (file instanceof File) {
      formData.append('files', file, file.name);
    }
  });

  for (const key in metadata) {
    const value = metadata[key];
    if (Array.isArray(value)) {
        value.forEach(item => formData.append(key, String(item)));
    } else if (value) {
        formData.append(key, value);
    }
  }
  
  // Use customFetch for the upload to ensure consistent auth handling
  return customFetch(url, {
    method: 'POST',
    body: formData,
  });
};

// --- All other specific API functions are now simplified ---

export const getProducts = async () => {
  return customFetch('/api/s3/products/');
};

export const createProduct = async (productData: { name: string; description: string; productid: string | null; s3path: string | null }) => {
  return customFetch('/api/s3/products/', {
    method: 'POST',
    body: JSON.stringify(productData),
  });
};

export const updateProduct = async (productId: number, productData: { name: string; description: string; productid: string | null; s3path: string | null }) => {
  return customFetch(`/api/s3/products/${productId}/`, {
    method: 'PUT',
    body: JSON.stringify(productData),
  });
};

export const deleteProduct = async (productId: number) => {
  return customFetch(`/api/s3/products/${productId}/`, {
    method: 'DELETE',
  });
};

export const createProductDirectory = async (productId: number) => {
  return customFetch(`/api/s3/products/${productId}/create_directory/`, {
    method: 'POST',
  });
};

export const getS3Configs = async () => {
  return customFetch('/api/s3/configs/');
};

export const updateS3Config = async (configId: number, configData: any) => {
  return customFetch(`/api/s3/configs/${configId}/`, {
    method: 'PATCH',
    body: JSON.stringify(configData),
  });
};

export const analyzeTableColumns = async (formData: FormData) => {
    const response = await customFetch('/api/sqlflow/analyze/table-columns/', {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Analysis failed');
    }

    return response.json();
};

export const convertToXML = async (formData: FormData) => {
    const response = await customFetch('/api/sqlflow/analyze/to-xml/', {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Conversion to XML failed');
    }

    const data = await response.json();

    if (data.code && data.code !== 200 && data.error) {
        throw new Error(data.error);
    }

    return data;
};

export const formatSQL = async (formData: FormData) => {
    const response = await customFetch('/api/sqlflow/analyze/format-sql/', {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'SQL formatting failed');
    }

    const data = await response.json();

    if (data.code && data.code !== 200 && data.error) {
        throw new Error(data.error);
    }

    return data;
};

export const getDataLineage = async (formData: FormData) => {
    const response = await customFetch('/api/sqlflow/analyze/data-lineage/', {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Data lineage analysis failed');
    }

    const data = await response.json();

    if (data.code && data.code !== 200 && data.error) {
        throw new Error(data.error);
    }

    return data;
};

export const exportFullLineageAsCsv = async (formData: FormData) => {
    const response = await customFetch('/api/sqlflow/analyze/lineage-csv/', {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'CSV lineage export failed');
    }

    const data = await response.json();

    if (data.code && data.code !== 200 && data.error) {
        throw new Error(data.error);
    }

    return data;
};

// Fetch and ensure CSRF token is available
export const fetchCsrfToken = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/get-csrf-token/`, {
            method: 'GET',
            credentials: 'include', // Important: include cookies
        });
        if (response.ok) {
            const data = await response.json();
            // Store the token in memory for cross-domain use (Chrome compatibility)
            const token = data.csrfToken || data.csrf_token;
            if (token) {
                setCsrfToken(token);
            }
            return data;
        }
    } catch (error) {
        console.error('Failed to fetch CSRF token:', error);
    }
    return null;
};

export const fetchMenuItems = async () => {
    return customFetch('/api/uiconfig/menu-items/');
};

// Function to fetch user details
export const fetchUser = async () => {
    return customFetch('/api/users/me/');
};

export const crawlReddit = async (url: string, appId: number) => {
  return customFetch('/api/reddit/crawl/', {
    method: 'POST',
    body: JSON.stringify({ url, app_id: appId }),
  });
}; 

export const saveRedditPost = async (postData: {
  url: string;
  title: string;
  created_time: string;
  json_data: string;
}) => {
  return customFetch('/api/reddit/save_post/', {
    method: 'POST',
    body: JSON.stringify(postData),
  });
};

export const getRedditPosts = async (params: {
  page?: number;
  search?: string;
  created_time_after?: string;
  created_time_before?: string;
  score__gte?: number;
  score__lte?: number;
}) => {
  const query = new URLSearchParams();
  if (params.page) query.append('page', params.page.toString());
  if (params.search) query.append('search', params.search);
  if (params.created_time_after) query.append('created_time_gte', params.created_time_after);
  if (params.created_time_before) query.append('created_time_lte', params.created_time_before);
  if (params.score__gte !== undefined) query.append('score__gte', params.score__gte.toString());
  if (params.score__lte !== undefined) query.append('score__lte', params.score__lte.toString());
  
  return customFetch(`/api/reddit/posts/?${query.toString()}`);
};

export const crawlSubreddit = async (data: {
  subreddit: string;
  limit: number;
  app_id: number;
}) => {
  return customFetch('/api/reddit/crawl_subreddit/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Reddit Job Management API functions
export const getRedditJobs = async (params?: { page?: number }) => {
  const query = new URLSearchParams();
  if (params?.page) query.append('page', params.page.toString());
  
  return customFetch(`/api/reddit/jobs/?${query.toString()}`);
};

export const createRedditJob = async (jobData: {
  name: string;
  subreddit: string;
  post_limit: number;
  schedule: string;
  timezone: string;
  is_enabled: boolean;
}) => {
  return customFetch('/api/reddit/jobs/', {
    method: 'POST',
    body: JSON.stringify(jobData),
  });
};

export const updateRedditJob = async (jobId: number, jobData: {
  name: string;
  subreddit: string;
  post_limit: number;
  schedule: string;
  timezone: string;
  is_enabled: boolean;
}) => {
  return customFetch(`/api/reddit/jobs/${jobId}/`, {
    method: 'PUT',
    body: JSON.stringify(jobData),
  });
};

export const deleteRedditJob = async (jobId: number) => {
  return customFetch(`/api/reddit/jobs/${jobId}/`, {
    method: 'DELETE',
  });
};

export const getRedditJobHistory = async (jobId?: number, params?: { page?: number }) => {
  const query = new URLSearchParams();
  if (params?.page) query.append('page', params.page.toString());
  
  const url = jobId 
    ? `/api/reddit/jobs/${jobId}/history/?${query.toString()}`
    : `/api/reddit/job-history/?${query.toString()}`;
  
  return customFetch(url);
};

export const getServerTimezone = async () => {
  return customFetch('/api/reddit/server-timezone/');
};

export const getLLMModels = () => {
    return customFetch('/api/llm/models/');
};

export const googleSearch = (query: string) => {
  return customFetch('/api/llm/search/', {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
};

export const summarizeText = (text: string, model_id?: number) => {
    return customFetch('/api/llm/summarize/', {
        method: 'POST',
        body: JSON.stringify({ text, model_id }),
    });
}; 

export const getGoogleSearchToken = async () => {
  return customFetch('/api/generate-google-token/', {
    method: 'POST',
  });
};

// CRM (Gudu Sales) APIs
export const getRecentOrders = async (params?: { page?: number; search?: string }) => {
  const query = new URLSearchParams();
  if (params?.page) query.append('page', String(params.page));
  if (params?.search) query.append('search', params.search);
  return customFetch(`/api/crm/orders/?${query.toString()}`);
};

// Create Stripe Checkout Sessions
export const createCheckoutPayment = async (payload: {
  amount?: number;
  currency?: string;
  email?: string;
  product_id?: string | number;
  product_name?: string;
  qty?: number;
  companyname?: string;
  price?: string; // existing price id
}) => {
  return customFetch('/api/crm/checkout/payment/', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  });
};

export const createCheckoutSubscription = async (payload: {
  price?: string; // optional existing price id
  amount?: number; // cents if price not provided
  currency?: string;
  email?: string;
  product_id?: string | number;
  product_name?: string;
  qty?: number;
  companyname?: string;
}) => {
  return customFetch('/api/crm/checkout/subscription/', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  });
};

export const updateRedditPostScore = async (urlMd5: string, score: number) => {
  return customFetch(`/api/reddit/posts/${urlMd5}/score/`, {
    method: 'PATCH',
    body: JSON.stringify({ score }),
  });
};

export const batchScoreRedditPosts = async (postCount: number = 10) => {
  return customFetch('/api/reddit/batch-scoring/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ post_count: postCount }),
  });
};

export const submitBatchScoringResults = async (llmResponse: string, postUrlMd5s: string[]) => {
  return customFetch('/api/reddit/batch-scoring-result/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      llm_response: llmResponse, 
      post_url_md5s: postUrlMd5s 
    }),
  });
};

export const resetAllRedditPostScores = async () => {
  return customFetch('/api/reddit/reset-all-scores/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// LLM Prompt API functions
export const getLLMPrompt = async (promptType: string) => {
  return customFetch(`/api/llm/prompts/${promptType}/`);
};

export const getAllLLMPrompts = async () => {
  return customFetch('/api/llm/prompts/');
};

export const renderLLMPrompt = async (promptType: string, templateData: any) => {
  return customFetch('/api/llm/prompts/render/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt_type: promptType,
      template_data: templateData,
    }),
  });
}; 